import AdminLayout from '@/components/admin/AdminLayout'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import apiClient from '@/lib/axios'
import { useAuth, useUser } from '@clerk/clerk-react'
import { Edit2, Plus, Search, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

// MongoDB Dish type
export interface Dish {
  _id: string
  restaurant_id: string
  name: string
  name_ar?: string
  name_en?: string
  description?: string
  price: number
  img_url?: string
  category?: {
    _id: string
    name: string
    synonyms?: string[]
  }
  ingredients?: string[]
  ingredients_ar?: string[]
  allergens?: string[]
  allergens_ar?: string[]
  dietary?: string[]
  nutrition?: Record<string, any>
  prep_time_minutes?: number
  tags?: string[]
  tags_ar?: string[]
  search_keywords?: string[]
  isAvailable: boolean
  createdAt: string
  updatedAt: string
}

export default function Dishes() {
  const navigate = useNavigate()
  const { getToken } = useAuth()
  const { user, isLoaded: isUserLoaded } = useUser()

  // Fixed restaurant ID for MongoDB dishes
  const MONGODB_RESTAURANT_ID = '693fda3e7b5d79f24b7e70e1'
  const selectedRestaurantId = MONGODB_RESTAURANT_ID

  // Detect user role
  const userRoles = (isUserLoaded && user)
    ? ((user.publicMetadata?.roles as string[] | undefined) || [])
    : []
  const hasAccess =
    Array.isArray(userRoles) &&
    (userRoles.includes('restaurant_admin') ||
      userRoles.includes('branch_manager') ||
      userRoles.includes('super_admin'))

  const [dishes, setDishes] = useState<Dish[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [total, setTotal] = useState(0)
  const [editingDish, setEditingDish] = useState<Dish | null>(null)
  const [deletingDishId, setDeletingDishId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Redirect if user doesn't have required role
  useEffect(() => {
    if (isUserLoaded && !hasAccess) {
      navigate('/admin/dashboard', { replace: true })
    }
  }, [isUserLoaded, hasAccess, navigate])

  // Fetch dishes from MongoDB
  const fetchDishes = useCallback(async () => {
    console.log('[Dishes] fetchDishes called with fixed restaurantId:', selectedRestaurantId)

    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        restaurantId: selectedRestaurantId,
      })

      if (searchQuery) {
        params.append('search', searchQuery)
      }

      const token = await getToken()

      console.log('[Dishes] API Request:', {
        selectedRestaurantId,
        params: params.toString(),
        url: `/menu-items?${params.toString()}`,
      })

      const response = await apiClient.get(`/menu-items?${params.toString()}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'X-Restaurant-ID': selectedRestaurantId,
        },
      })

      setDishes(response.data.data || [])
      setTotal(response.data.meta?.total || 0)
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      setError(error?.response?.data?.message || error?.message || 'Failed to fetch dishes')
      setDishes([])
    } finally {
      setLoading(false)
    }
  }, [page, limit, searchQuery, getToken, selectedRestaurantId])

  useEffect(() => {
    console.log('[Dishes] useEffect triggered - using fixed restaurantId:', selectedRestaurantId)
    fetchDishes()
  }, [fetchDishes, selectedRestaurantId])

  const handleDelete = async (dishId: string) => {
    setIsDeleting(true)
    try {
      const token = await getToken()
      await apiClient.delete(`/menu-items/${dishId}?restaurantId=${selectedRestaurantId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'X-Restaurant-ID': selectedRestaurantId,
        },
      })
      setDeletingDishId(null)
      fetchDishes()
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      setError(error?.response?.data?.message || error?.message || 'Failed to delete dish')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleUpdate = async (dishId: string, updates: Partial<Dish>) => {
    try {
      const token = await getToken()
      await apiClient.patch(`/menu-items/${dishId}?restaurantId=${selectedRestaurantId}`, updates, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'X-Restaurant-ID': selectedRestaurantId,
        },
      })
      setEditingDish(null)
      fetchDishes()
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      setError(error?.response?.data?.message || error?.message || 'Failed to update dish')
    }
  }

  const hasMore = page * limit < total

  // Show loading state while checking user
  if (!isUserLoaded) {
    return (
      <AdminLayout>
        <div className="p-8">Loading...</div>
      </AdminLayout>
    )
  }

  // Show access denied if user doesn't have required role
  if (!hasAccess) {
    return (
      <AdminLayout>
        <div className="p-8">
          <Alert variant="destructive">
            <AlertDescription>
              You do not have permission to access dishes. Only restaurant admins, branch managers, or super admins can access this page.
            </AlertDescription>
          </Alert>
        </div>
      </AdminLayout>
    )
  }

  // Show loading state
  if (loading && dishes.length === 0) {
    return (
      <AdminLayout>
        <div className="p-8">Loading dishes...</div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6" style={{ padding: 'var(--spacing-lg)' }}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
              MongoDB Dishes
            </h1>
            <p className="text-muted-foreground" style={{ color: 'var(--text-secondary)' }}>
              Manage restaurant dishes stored in MongoDB
            </p>
          </div>

          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
                Add Dish
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg md:max-w-2xl lg:max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Dish</DialogTitle>
                <DialogDescription>
                  Add a new dish to your MongoDB collection
                </DialogDescription>
              </DialogHeader>
              <div className="p-4">
                <p className="text-sm text-muted-foreground">Dish form coming soon...</p>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* MongoDB Mode Indicator */}
        <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
          <span className="text-sm font-medium text-green-700">
            🍃 MongoDB Collection - Restaurant {MONGODB_RESTAURANT_ID} ({total} total dishes)
          </span>
        </div>

        {/* Search Bar */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground rtl:left-auto rtl:right-3" />
            <Input
              placeholder="Search dishes..."
              className="pl-10 rtl:pl-0 rtl:pr-10"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(1)
              }}
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            {error}
          </div>
        )}

        {/* Dishes Grid/List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dishes.map((dish) => (
            <div
              key={dish._id}
              className="border rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              {dish.img_url && (
                <div className="w-full h-48 rounded overflow-hidden mb-3">
                  <img
                    src={dish.img_url}
                    alt={dish.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <h3 className="font-semibold text-lg">{dish.name}</h3>
              {dish.name_ar && (
                <p className="text-sm text-muted-foreground" dir="rtl">
                  {dish.name_ar}
                </p>
              )}
              {dish.description && (
                <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                  {dish.description}
                </p>
              )}
              <div className="mt-3 flex items-center justify-between">
                <span className="text-lg font-bold">
                  {(dish.price / 100).toFixed(2)} EGP
                </span>
                <span
                  className={`px-2 py-1 text-xs rounded ${
                    dish.isAvailable
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {dish.isAvailable ? 'Available' : 'Unavailable'}
                </span>
              </div>
              {dish.category && typeof dish.category === 'object' && (
                <div className="mt-2">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    {dish.category.name}
                  </span>
                </div>
              )}
              {dish.ingredients && dish.ingredients.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {dish.ingredients.slice(0, 3).map((ing, idx) => (
                    <span
                      key={idx}
                      className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                    >
                      {ing}
                    </span>
                  ))}
                  {dish.ingredients.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{dish.ingredients.length - 3} more
                    </span>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-4 pt-3 border-t flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setEditingDish(dish)}
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setDeletingDishId(dish._id)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {!loading && dishes.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              No dishes found. {searchQuery ? 'Try a different search.' : 'Add your first dish to get started.'}
            </p>
          </div>
        )}

        {/* Edit Dialog */}
        {editingDish && (
          <Dialog open={!!editingDish} onOpenChange={(open) => !open && setEditingDish(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Dish</DialogTitle>
                <DialogDescription>
                  Update the dish information below
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-name">Name (English)</Label>
                  <Input
                    id="edit-name"
                    value={editingDish.name}
                    onChange={(e) => setEditingDish({ ...editingDish, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-name-ar">Name (Arabic)</Label>
                  <Input
                    id="edit-name-ar"
                    value={editingDish.name_ar || ''}
                    onChange={(e) => setEditingDish({ ...editingDish, name_ar: e.target.value })}
                    dir="rtl"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editingDish.description || ''}
                    onChange={(e) => setEditingDish({ ...editingDish, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-price">Price (EGP)</Label>
                  <Input
                    id="edit-price"
                    type="number"
                    step="0.01"
                    value={(editingDish.price / 100).toFixed(2)}
                    onChange={(e) => setEditingDish({ ...editingDish, price: Math.round(parseFloat(e.target.value) * 100) })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit-available"
                    checked={editingDish.isAvailable}
                    onChange={(e) => setEditingDish({ ...editingDish, isAvailable: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="edit-available">Available</Label>
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setEditingDish(null)}>
                    Cancel
                  </Button>
                  <Button onClick={() => handleUpdate(editingDish._id, editingDish)}>
                    Save Changes
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingDishId} onOpenChange={(open: boolean) => !open && setDeletingDishId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Dish</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this dish? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deletingDishId && handleDelete(deletingDishId)}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {dishes.length > 0 ? (page - 1) * limit + 1 : 0} to{' '}
            {Math.min(page * limit, total)} of {total} dishes
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              disabled={!hasMore}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
